import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Play, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import { logoutUser } from "../lib/api";
import { useAuth } from "../auth";

export const Header = ({ onPlay, theater }) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const onHistory = location.pathname === "/history";
  const onSaves = location.pathname.startsWith("/saves");
  const onPlayer = location.pathname === "/";

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim() || loading) return;
    setLoading(true);
    try {
      const v = await onPlay(url.trim());
      setUrl("");
      navigate(`/?v=${v.video_id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That doesn't look like a supported video URL");
    } finally {
      setLoading(false);
    }
  };

  const doLogout = async () => {
    await logoutUser().catch(() => {});
    setUser(false);
    navigate("/login");
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 h-20 flex items-center justify-between px-5 md:px-12 bg-black/80 backdrop-blur-xl border-b border-white/5 transition-opacity duration-500 ${theater ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
      <div className="flex items-center gap-5">
        <Link to="/" data-testid="brand-link" className="hidden sm:flex items-center gap-2 text-white">
          <Clapperboard className="w-4 h-4" strokeWidth={1.5} />
          <span className="font-meta text-[10px] tracking-[0.35em] uppercase">PurePlay</span>
        </Link>
        <form
          onSubmit={submit}
          className="w-[240px] sm:w-[340px] md:w-[450px] bg-white/5 border border-white/10 rounded-full px-5 py-2.5 flex items-center gap-3 focus-within:border-white/30 focus-within:bg-white/10 transition-colors duration-300"
        >
          <input
            data-testid="url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste a video link — YouTube, Vimeo, Dailymotion…"
            className="bg-transparent border-none text-white placeholder-zinc-500 focus:outline-none focus:ring-0 w-full font-ui text-sm"
            spellCheck={false}
          />
          <button
            data-testid="play-button"
            type="submit"
            disabled={loading}
            aria-label="Play video"
            className="shrink-0 w-8 h-8 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 active:scale-95 transition-transform duration-200 disabled:opacity-40"
          >
            <Play className="w-3.5 h-3.5 fill-black" />
          </button>
        </form>
      </div>
      <div className="flex items-center gap-5 md:gap-8">
        <Link to="/profile" data-testid="profile-link" className="flex items-center gap-3 group">
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              data-testid="user-avatar"
              referrerPolicy="no-referrer"
              className="w-7 h-7 rounded-full border border-white/20 object-cover"
            />
          ) : (
            <span
              data-testid="user-avatar"
              className="w-7 h-7 rounded-full bg-white/10 border border-white/15 flex items-center justify-center font-meta text-[10px] text-zinc-300"
            >
              {(user?.first_name?.[0] || "?").toUpperCase()}
              {(user?.last_name?.[0] || "").toUpperCase()}
            </span>
          )}
          <span
            data-testid="user-name-label"
            className="hidden md:inline font-meta text-[10px] tracking-[0.25em] uppercase text-zinc-600 group-hover:text-white transition-colors duration-200"
          >
            {user?.first_name} {user?.last_name}
          </span>
        </Link>
        {!onPlayer && (
          <Link
            to="/"
            data-testid="player-link"
            className="font-meta text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors duration-200"
          >
            ← Player
          </Link>
        )}
        {!onSaves && (
          <Link
            to="/saves"
            data-testid="saves-link"
            className="font-meta text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors duration-200"
          >
            Saves
          </Link>
        )}
        {!onHistory && (
          <Link
            to="/history"
            data-testid="history-link"
            className="font-meta text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors duration-200"
          >
            History
          </Link>
        )}
        {user?.role === "admin" && (
          <Link
            to="/admin"
            data-testid="admin-link"
            className="font-meta text-xs tracking-[0.2em] uppercase text-amber-400/80 hover:text-amber-300 transition-colors duration-200"
          >
            Admin
          </Link>
        )}
        <button
          data-testid="logout-button"
          onClick={doLogout}
          className="font-meta text-xs tracking-[0.2em] uppercase text-zinc-600 hover:text-red-400 transition-colors duration-200"
        >
          Logout
        </button>
      </div>
    </header>
  );
};
